/**
 * /admin/campaigns/:id/edit — edit an existing draft.
 *
 * Fetches the campaign on mount, hands the data to CampaignForm in edit
 * mode (which pre-populates every field). Submit calls updateCampaign.
 * The Lambda only allows updates while status=draft and returns 409
 * otherwise — we display that as an inline error.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Campaign,
  CreateCampaignInput,
  getCampaign,
  updateCampaign,
} from "../../../api/campaigns";
import CampaignForm from "./CampaignForm";

const CampaignEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const nav = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const c = await getCampaign(id);
        setCampaign(c);
        if (c.status !== "draft") {
          setError(
            `Cannot edit campaign in status "${c.status}". Only drafts are editable.`
          );
        }
      } catch (err: any) {
        setError(
          err.response?.data?.error || err.message || "Failed to load"
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  async function submit(input: CreateCampaignInput) {
    if (!id) return;
    await updateCampaign(id, input);
    nav(`/admin/campaigns/${id}`);
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="min-h-screen p-8">
        <div className="bg-red-50 text-red-700 p-4 rounded-md">
          {error || "Campaign not found"}
        </div>
        <Link
          to="/admin/campaigns"
          className="text-purple-700 hover:underline text-sm mt-4 inline-block"
        >
          ← Back to list
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto py-10 px-6">
        <Link
          to={`/admin/campaigns/${id}`}
          className="text-sm text-purple-700 hover:underline"
        >
          ← Back to detail
        </Link>
        <h1 className="text-3xl font-bold text-gray-800 mt-3 mb-2">
          Edit campaign
        </h1>
        <div className="text-sm text-gray-500 font-mono mb-8">
          {campaign.campaign_id}
        </div>

        {campaign.status !== "draft" ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
            Cannot edit campaign in status "{campaign.status}". Only drafts are
            editable. Cancel it and create a new one to start over.
          </div>
        ) : (
          <CampaignForm
            mode="edit"
            initial={campaign}
            onSubmit={submit}
            onCancel={() => nav(`/admin/campaigns/${id}`)}
            submitLabel="Save changes"
          />
        )}
      </div>
    </div>
  );
};

export default CampaignEdit;
